export interface ChangeItem {
  type: 'added' | 'removed' | 'modified';
  featureKey: string;
  paramKey?: string;
  oldValue?: any;
  newValue?: any;
  isFeature?: boolean;
}

export const detectChanges = (
  originalData: any,
  currentData: any,
  deletedItems: { [key: string]: string[] | 'DELETED_FEATURE' },
  appConfig?: any
): ChangeItem[] => {
  const changes: ChangeItem[] = [];

  // Get all feature keys from both original and current data
  const allFeatureKeys = new Set([
    ...Object.keys(originalData || {}),
    ...Object.keys(currentData || {}),
    ...Object.keys(appConfig || {})
  ]);

  allFeatureKeys.forEach(featureKey => {
    const originalFeature = originalData?.[featureKey];
    const currentFeature = currentData?.[featureKey];
    const schemaFeature = appConfig?.[featureKey];
    const deletedFeature = deletedItems[featureKey] === 'DELETED_FEATURE';

    // Check if feature was deleted
    if (deletedFeature) {
      changes.push({
        type: 'removed',
        featureKey,
        oldValue: originalFeature,
        isFeature: true
      });
      return;
    }

    // Check if feature was added (exists in current but not in original)
    if (!originalFeature && currentFeature) {
      changes.push({
        type: 'added',
        featureKey,
        newValue: currentFeature,
        isFeature: true
      });
    }

    // Check if feature was modified
    if (originalFeature && currentFeature) {
      // Check if feature enable status changed
      const originalEnable = originalFeature.enable;
      const currentEnable = currentFeature.enable;
      

      
      if (originalEnable !== currentEnable) {
        changes.push({
          type: 'modified',
          featureKey,
          oldValue: originalEnable,
          newValue: currentEnable,
          isFeature: true
        });
      }

      // Check parameter changes
      const originalParams = originalFeature.params || {};
      const currentParams = currentFeature.params || {};
      const deletedParams = Array.isArray(deletedItems[featureKey]) ? deletedItems[featureKey] : [];

      // Get all parameter keys
      const allParamKeys = new Set([
        ...Object.keys(originalParams),
        ...Object.keys(currentParams),
        ...(schemaFeature?.params?.map((p: any) => p.key) || [])
      ]);

      allParamKeys.forEach(paramKey => {
        const originalValue = originalParams[paramKey];
        const currentValue = currentParams[paramKey];
        const isDeleted = deletedParams.includes(paramKey);

        // Check if parameter was deleted
        if (isDeleted) {
          changes.push({
            type: 'removed',
            featureKey,
            paramKey,
            oldValue: originalValue
          });
          return;
        }

        // Check if parameter was added (exists in current but not in original)
        if (originalValue === undefined && currentValue !== undefined) {
          changes.push({
            type: 'added',
            featureKey,
            paramKey,
            newValue: currentValue
          });
        }

        // Check if parameter was modified
        if (originalValue !== undefined && currentValue !== undefined) {
          // Handle different data types for comparison
          let isModified = false;
          
          if (typeof originalValue === 'number' && typeof currentValue === 'number') {
            isModified = Math.abs(originalValue - currentValue) > 0.000001; // Handle floating point precision
          } else if (typeof originalValue === 'boolean' && typeof currentValue === 'boolean') {
            isModified = originalValue !== currentValue;
          } else if (typeof originalValue === 'string' && typeof currentValue === 'string') {
            isModified = originalValue !== currentValue;
          } else {
            isModified = originalValue !== currentValue;
          }
          

          
          if (isModified) {
            changes.push({
              type: 'modified',
              featureKey,
              paramKey,
              oldValue: originalValue,
              newValue: currentValue
            });
          }
        }
      });
    }
  });

  return changes;
}; 